import { useState, useCallback, useEffect } from "react";
import { Pencil, Trash2, CheckCircle2, Wrench, ChevronDown, X, FileSpreadsheet, FileText } from "lucide-react";
import { PrintOverlay } from "@/components/printDocs";
import { HistoryPrintView } from "@/components/HistoryPrintView";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const PAGE = 15;

/** Riwayat berhalaman + filter tanggal untuk /scans. `onLoaded` dipanggil tiap refresh. */
export function useHistory(path, onLoaded) {
  const [rows, setRows] = useState(null);
  const [date, setDate] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const fetchPage = useCallback(async (skip) => {
    const { data } = await api.get(`${path}?limit=${PAGE}&skip=${skip}${date ? `&date=${date}` : ""}`);
    setHasMore(data.length === PAGE);
    return data;
  }, [path, date]);
  const refresh = useCallback(() => {
    fetchPage(0).then((d) => { setRows(d); onLoaded?.(); }).catch(() => {});
  }, [fetchPage, onLoaded]);
  const loadMore = async () => {
    setLoading(true);
    try { const d = await fetchPage(rows?.length || 0); setRows((r) => [...(r || []), ...d]); }
    catch (e) { toast.error(fmtErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { setRows(null); refresh(); }, [refresh]);
  return { rows, date, setDate, hasMore, loading, refresh, loadMore };
}

/** Filter tanggal + unduh Excel / cetak PDF untuk tabel riwayat. `kind` = "qc" | "delivery" */
export function HistoryControls({ h, testid, kind, path, user }) {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState("");
  const suffix = h.date || "semua";
  const downloadExcel = async () => {
    setBusy("xlsx");
    try {
      const res = await api.get(`${path}/export${h.date ? `?date=${h.date}` : ""}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = `riwayat_${kind}_${suffix}.xlsx`; a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Excel riwayat terunduh / 记录Excel已下载");
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(""); }
  };
  const openPdf = async () => {
    setBusy("pdf");
    try {
      const { data } = await api.get(`${path}?limit=5000${h.date ? `&date=${h.date}` : ""}`);
      setDoc(data);
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(""); }
  };
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={`${testid}-filter`}>
      <Input type="date" value={h.date} onChange={(e) => h.setDate(e.target.value)} title="Filter tanggal riwayat" className="h-8 w-36 border-slate-800 bg-slate-900/70 font-mono text-xs" data-testid={`${testid}-date`} />
      {h.date && (
        <button type="button" onClick={() => h.setDate("")} title="Semua tanggal" className="rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-200" data-testid={`${testid}-date-clear`}><X className="h-3.5 w-3.5" /></button>
      )}
      <Button type="button" size="sm" variant="outline" onClick={downloadExcel} disabled={!!busy} title="Unduh Excel riwayat sesuai filter tanggal" className="h-8 border-emerald-500/40 px-2 text-xs text-emerald-300 hover:bg-emerald-500/10" data-testid={`${testid}-export-xlsx`}>
        <FileSpreadsheet className="mr-1 h-3.5 w-3.5" />Excel
      </Button>
      <Button type="button" size="sm" variant="outline" onClick={openPdf} disabled={!!busy} title="Cetak PDF riwayat sesuai filter tanggal" className="h-8 border-sky-500/40 px-2 text-xs text-sky-300 hover:bg-sky-500/10" data-testid={`${testid}-export-pdf`}>
        <FileText className="mr-1 h-3.5 w-3.5" />PDF
      </Button>
      {doc && (
        <PrintOverlay testid={`${testid}-print-overlay`} onClose={() => setDoc(null)}>
          <HistoryPrintView kind={kind} rows={doc} date={h.date} user={user} />
        </PrintOverlay>
      )}
    </div>
  );
}

export function LoadMore({ h, testid }) {
  if (!h.rows?.length) return null;
  return (
    <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500" data-testid={`${testid}-footer`}>
      <span>{h.rows.length} riwayat{h.date ? ` · ${h.date}` : ""}</span>
      {h.hasMore && (
        <Button type="button" size="sm" variant="ghost" onClick={h.loadMore} disabled={h.loading} className="h-7 text-xs text-sky-400 hover:bg-sky-500/10" data-testid={`${testid}-load-more`}>
          <ChevronDown className="mr-1 h-3.5 w-3.5" />{h.loading ? "Memuat..." : "Muat lebih banyak"} <span className="cn ml-1">加载更多</span>
        </Button>
      )}
    </div>
  );
}

/** Tombol edit + hapus satu baris riwayat (QC scan / delivery). Hanya admin atau operator yang melakukannya. */
export function HistoryActions({ row, user, onEdit, onDelete, testid }) {
  const allowed = user?.role === "admin" || row.byEmail === user?.email || row.by === user?.name;
  if (!allowed) return <span className="text-xs text-slate-700">—</span>;
  return (
    <div className="flex items-center gap-1" data-testid={`${testid}-actions`}>
      {onEdit && (
        <button type="button" onClick={() => onEdit(row)} title="Edit / 编辑" data-testid={`${testid}-edit`}
          className="rounded p-1 text-slate-500 transition-colors hover:bg-sky-500/10 hover:text-sky-400"><Pencil className="h-3.5 w-3.5" /></button>
      )}
      <button type="button" onClick={() => onDelete(row)} title="Hapus / 删除" data-testid={`${testid}-delete`}
        className="rounded p-1 text-slate-500 transition-colors hover:bg-rose-500/10 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}

/** Dialog edit hasil QC (OK ↔ Repair) + catatan satu riwayat scan */
export function EditScanDialog({ scan, onClose, onSaved }) {
  const [result, setResult] = useState(scan.result);
  const [note, setNote] = useState(scan.note || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/scans/${scan.id}`, { result, note });
      toast.success(`Riwayat ${scan.code} diperbarui`);
      onSaved();
      onClose();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md border-slate-800 bg-[#0D1524] text-slate-100" data-testid="edit-scan-dialog">
        <DialogHeader><DialogTitle className="font-display">Edit Riwayat Scan · <span className="font-mono text-amber-400">{scan.code}</span> <span className="cn text-xs font-normal text-slate-500">编辑扫描记录</span></DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant="outline" onClick={() => setResult("OK")} data-testid="edit-scan-ok"
              className={cn("border-slate-800", result === "OK" && "border-emerald-500/60 bg-emerald-500/15 text-emerald-300")}>
              <CheckCircle2 className="mr-2 h-4 w-4" />OK / 合格
            </Button>
            <Button type="button" variant="outline" onClick={() => setResult("Repair")} data-testid="edit-scan-repair"
              className={cn("border-slate-800", result === "Repair" && "border-amber-500/60 bg-amber-500/15 text-amber-300")}>
              <Wrench className="mr-2 h-4 w-4" />Repair / 修复
            </Button>
          </div>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan defect (opsional)" className="border-slate-800 bg-slate-900/70 text-xs" data-testid="edit-scan-note" />
          <p className="text-[11px] text-slate-500">Mengubah OK ↔ Repair akan menyesuaikan jumlah QC OK / NG marking. <span className="cn">更改结果将调整构件的合格/返修数量。</span></p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} data-testid="edit-scan-cancel">Batal</Button>
            <Button type="button" onClick={save} disabled={busy} className="bg-amber-500 text-slate-950 hover:bg-amber-400" data-testid="edit-scan-save">Simpan / 保存</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteHistoryDialog({ row, path, label, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`${path}/${row.id}`);
      toast.success(`Riwayat ${row.code} dihapus`);
      onDeleted();
      onClose();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm border-rose-500/30 bg-[#0D1524] text-slate-100" data-testid="delete-history-dialog">
        <DialogHeader><DialogTitle className="font-display text-rose-300">Hapus Riwayat {label}? <span className="cn text-xs font-normal text-slate-500">删除记录</span></DialogTitle></DialogHeader>
        <p className="text-sm text-slate-300">Marking <span className="font-mono text-amber-400">{row.code}</span> · {row.qty} pcs · {row.result || "Delivery"} oleh {row.by}.</p>
        <p className="text-[11px] text-slate-500">Jumlah progres marking akan dikembalikan seperti sebelum scan ini. <span className="cn">构件进度将恢复到此次扫描之前。</span></p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} data-testid="delete-history-cancel">Batal</Button>
          <Button type="button" onClick={del} disabled={busy} className="bg-rose-600 text-white hover:bg-rose-500" data-testid="delete-history-confirm"><Trash2 className="mr-2 h-4 w-4" />Hapus / 删除</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
